import { Flavor } from '@pflegia/types/common/enums/flavor.enum';

const flavors = Object.values(Flavor);
const FLAVOR_SEGMENTS_PATTERN = `(?:${flavors.join('|')})`;
const FLAVOR_PREFIX_REGEX = new RegExp(String.raw`^(?:/${FLAVOR_SEGMENTS_PATTERN}|/\[flavor\])+(?=/|$|[?#])`, 'i');

export const stripFlavorPrefix = (pathname: string): string => stripFlavor(pathname) || '/';

/**
 * Strips the leading flavor segment(s) from a pathname.
 * Handles both literal paths and Next.js route templates.
 *
 * This utility removes the leading flavor (pflegia/praxia) prefix from paths,
 * as well as the Next.js route template placeholder ([flavor]),
 * to get the clean route path.
 *
 * @example
 * ```ts
 * // Client-side: Use hooks instead when possible
 * import { useRouterHelper } from '@shared/hooks/use-router-helper';
 * import { useStrippedRouter } from '@pflegia/ui/hooks/use-stripped-router';
 *
 * // In React components:
 * const { pathname, asPath } = useRouterHelper(); // Preferred - includes route type checks
 * // or
 * const { pathname, asPath } = useStrippedRouter(); // Simple path stripping only
 *
 * // Server-side (SSR/SSG): Use this function directly
 * import { stripFlavor } from '@pflegia/util/common/path-utils';
 *
 * export const getServerSideProps = ({ resolvedUrl }) => {
 *   const cleanPath = stripFlavor(resolvedUrl);
 *   // cleanPath: '/job-details/...' instead of '/pflegia/job-details/...'
 * };
 * ```
 *
 * @param pathname - The pathname to strip the flavor from (e.g., '/pflegia/jobs')
 * @returns The pathname without the flavor segment (e.g., '/jobs')
 */
export const stripFlavor = (pathname: string): string => pathname.replace(FLAVOR_PREFIX_REGEX, '');

/**
 * Server-side route checker utilities.
 * These functions automatically strip the flavor before checking routes.
 * Use these in SSR/SSG contexts instead of manual stripFlavor + startsWith.
 *
 * @example
 * ```ts
 * // Before (manual)
 * const stripped = stripFlavor(resolvedUrl);
 * if (stripped.startsWith('/job-details')) { }
 *
 * // After (centralized)
 * if (isJobDetailsRoute(resolvedUrl)) { }
 * ```
 */

/**
 * Checks if a URL is a job details route.
 * @param url - The URL to check (can include a flavor prefix)
 * @returns true if the URL is a job details route
 */
export const isJobDetailsRoute = (url: string): boolean => stripFlavor(url).startsWith('/job-details');

/**
 * Checks if a URL is a job route (not job-details).
 * @param url - The URL to check (can include a flavor prefix)
 * @returns true if the URL is a job route
 */
export const isJobRoute = (url: string): boolean => {
  const stripped = stripFlavor(url);
  return stripped.startsWith('/job/') || stripped === '/job';
};

/**
 * Checks if a URL is a jobs route (transparent job board).
 * @param url - The URL to check (can include a flavor prefix)
 * @returns true if the URL is a jobs route
 */
export const isJobsRoute = (url: string): boolean => stripFlavor(url).startsWith('/jobs');

/**
 * Checks if a URL is a job board route.
 * @param url - The URL to check (can include a flavor prefix)
 * @returns true if the URL is a job board route
 */
export const isJobBoardRoute = (url: string): boolean => stripFlavor(url).startsWith('/job-board');

/**
 * Checks if a URL is an external job route.
 * @param url - The URL to check (can include a flavor prefix)
 * @returns true if the URL is an external job route
 */
export const isExternalJobRoute = (url: string): boolean => stripFlavor(url).startsWith('/external-job');

/**
 * Checks if a URL is a blog/magazin route.
 * @param url - The URL to check (can include a flavor prefix)
 * @returns true if the URL is a blog route
 */
export const isBlogRoute = (url: string): boolean => stripFlavor(url).startsWith('/magazin');
